// Sends member activity to the Maor API: app sessions, and playback of videos and audio.
//
// Events are written to a queue in localStorage first and sent in batches. Anything not
// acknowledged stays queued and is retried later, including on the member's next visit,
// so a closed tab or a dropped connection delays events rather than losing them. Every
// event carries its own id, so sending one twice is harmless: the API stores it once.
//
// Ported from the backend's activity-tracking-frontend-guide.md (version 2), with two changes:
// beforeLogout() works when the token is cleared straight after it and never waits more than
// LOGOUT_WAIT_MS, and sendQueue() checks who is signed in before every batch.

const OUTBOX_KEY = "maor.activity.outbox.v1";
const LAST_SEEN_KEY = "maor.activity.lastSeen.v1";
const MAX_QUEUED = 500; // oldest events are dropped beyond this
const MAX_BATCH = 50; // the API's limit per request
const MAX_KEEPALIVE_BYTES = 32000; // browsers allow 64 KB for requests that outlive the page
const SEND_DELAY_MS = 5 * 1000; // gather events for a few seconds, then send them together
const FIRST_RETRY_MS = 10 * 1000;
const MAX_RETRY_MS = 5 * 60 * 1000;
const SESSION_GAP_MS = 30 * 60 * 1000; // inactive this long, and coming back is a new session
const LOGOUT_WAIT_MS = 3 * 1000; // signing out never waits longer than this for the last send
// Signs that the member is here. Checked at most once a minute, so a long stretch with the
// tab open but nobody at it still counts as being away.
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll", "focus"];
const ACTIVITY_CHECK_MS = 60 * 1000;

export class ActivityClient {
  /**
   * @param {object} options
   * @param {string} options.endpoint The full URL of the endpoint, e.g. https://<api host>/api/v1/activity/events
   * @param {() => ({ token: string, memberId: number } | null)} options.getAuth The signed-in
   *   member - their API token as the site sends it ("Bearer 123|abc..." or "123|abc...") and
   *   their user.id - or null when nobody is signed in. Read on every event and every send.
   * @param {"web" | "ios" | "android"} [options.platform] "web" for the website.
   */
  constructor(options) {
    this.options = options;
    this.platform = options.platform || "web";
    this.inFlight = new Set();
    this.storageWorks = true;
    this.memoryOutbox = null;
    this.memoryLastSeen = null;
    this.running = null;
    this.timer = null;
    this.retryMs = 0;
  }

  /**
   * Call once when the app starts. Sends anything left over from the last visit, reports a
   * session if the member was away, watches for the member coming back, and sends the queue
   * whenever the page is hidden or closed. Returns a function that undoes it.
   */
  start() {
    let lastActivityCheck = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityCheck >= ACTIVITY_CHECK_MS) {
        lastActivityCheck = now;
        this.reportSession();
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        this.touch();
        this.flush({ unloading: true });
      } else {
        this.reportSession();
      }
    };
    const onPageHide = () => {
      this.touch();
      this.flush({ unloading: true });
    };
    const onOnline = () => {
      this.flush();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("online", onOnline);
    // Capture phase, so nothing the page does with stopPropagation() hides the member.
    ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, { capture: true, passive: true }));

    this.reportSession();
    this.flush();

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("online", onOnline);
      ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, { capture: true }));
    };
  }

  /** Queue an event for the signed-in member. Does nothing when nobody is signed in. */
  track(event) {
    const auth = this.options.getAuth();
    if (auth === null) {
      return;
    }

    const outbox = this.readOutbox();
    const events = outbox !== null && outbox.memberId === auth.memberId ? outbox.events : [];

    // Playing something after a long break is coming back, even if nothing else said so.
    if (event.event !== "app.session" && this.isAway(auth)) {
      events.push(this.stamp({ event: "app.session" }));
    }

    events.push(this.stamp(event));

    this.writeOutbox({ memberId: auth.memberId, events: events.slice(-MAX_QUEUED) });
    this.touch();
    this.schedule(SEND_DELAY_MS);
  }

  /**
   * Report an app session if the member has been inactive for 30 minutes or more. Safe to
   * call often: start() calls it when the app opens, when the page becomes visible again and
   * when the member interacts with it, and you call it once right after sign-in.
   */
  reportSession() {
    const auth = this.options.getAuth();
    if (auth === null) {
      return;
    }

    if (this.isAway(auth)) {
      this.track({ event: "app.session" });
    } else {
      this.touch();
    }
  }

  /**
   * Call when the member signs out, before their token is cleared - clearing it straight after
   * the call is fine. Takes what is queued off the queue at once, sends it with the member's
   * token and forgets the member. Resolves once the events have gone, or after LOGOUT_WAIT_MS
   * at most, so a slow or unreachable API never holds a sign-out up.
   */
  beforeLogout() {
    const auth = this.options.getAuth();
    const outbox = this.readOutbox();

    this.clearTimer();
    this.retryMs = 0;
    this.writeOutbox(null);
    this.writeLastSeen(null);

    if (auth === null || outbox === null || outbox.memberId !== auth.memberId) {
      return Promise.resolve();
    }

    // Events already on their way in another request go again: the API stores each one once.
    return withTimeout(this.sendAll(outbox.events, auth), LOGOUT_WAIT_MS);
  }

  /**
   * Send the queue now, and resolve once it has gone - or failed and been left for a retry.
   * `unloading` is for a page being hidden or closed: a single request, sent with keepalive
   * so it is delivered even if the page is gone by then.
   */
  async flush(options = {}) {
    if (options.unloading === true) {
      await this.sendQueue(true);
      return;
    }

    if (this.running !== null) {
      // A send is already under way. Wait for it, then send whatever was queued meanwhile.
      await this.running;
      return this.flush();
    }

    this.clearTimer();
    this.running = this.sendQueue(false);

    try {
      await this.running;
    } finally {
      this.running = null;
    }
  }

  async sendQueue(unloading) {
    for (;;) {
      // Asked before every batch: a member can sign out, and another sign in, while one is out.
      const auth = this.options.getAuth();
      if (auth === null) {
        return;
      }

      const outbox = this.readOutbox();
      if (outbox === null) {
        return;
      }

      if (outbox.memberId !== auth.memberId) {
        // Queued for a member who has since signed out: never send it as someone else.
        this.writeOutbox(null);
        return;
      }

      const batch = this.nextBatch(outbox.events, unloading);
      if (batch.length === 0) {
        return;
      }

      const outcome = await this.send(batch, auth, unloading);
      if (outcome === "retry") {
        if (!unloading) {
          this.retryLater();
        }
        return;
      }

      this.removeFromOutbox(batch);
      this.retryMs = 0;

      if (unloading) {
        return;
      }
    }
  }

  /** Send `events` in batches with `auth`, stopping at the first batch that has to wait. */
  async sendAll(events, auth) {
    for (let start = 0; start < events.length; start += MAX_BATCH) {
      const outcome = await this.send(events.slice(start, start + MAX_BATCH), auth, false);
      if (outcome === "retry") {
        return; // offline, or the API is struggling - and the member is leaving, so no retry
      }
    }
  }

  nextBatch(events, unloading) {
    const batch = [];
    let bytes = 0;

    for (const event of events) {
      if (this.inFlight.has(event.event_id)) {
        continue;
      }
      if (batch.length >= MAX_BATCH) {
        break;
      }

      const size = JSON.stringify(event).length + 1;
      if (unloading && bytes + size > MAX_KEEPALIVE_BYTES) {
        break;
      }

      batch.push(event);
      bytes += size;
    }

    return batch;
  }

  /** @returns {Promise<"sent" | "dropped" | "retry">} */
  async send(batch, auth, keepalive) {
    batch.forEach((event) => this.inFlight.add(event.event_id));

    try {
      const response = await fetch(this.options.endpoint, {
        method: "POST",
        keepalive,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: auth.token.startsWith("Bearer ") ? auth.token : `Bearer ${auth.token}`,
        },
        body: JSON.stringify({ sent_at: new Date().toISOString(), events: batch }),
      });

      if (response.ok) {
        return "sent";
      }

      // Blocked account (403) or a batch the API will never accept (413, 422): resending
      // cannot help. Everything else - 401 while a token is refreshed, 429, 5xx - is retried.
      return response.status === 403 || response.status === 413 || response.status === 422 ? "dropped" : "retry";
    } catch {
      return "retry"; // offline, or the request never got out
    } finally {
      batch.forEach((event) => this.inFlight.delete(event.event_id));
    }
  }

  removeFromOutbox(batch) {
    const done = new Set(batch.map((event) => event.event_id));
    const outbox = this.readOutbox();

    if (outbox !== null) {
      this.writeOutbox({
        memberId: outbox.memberId,
        events: outbox.events.filter((event) => !done.has(event.event_id)),
      });
    }
  }

  schedule(delayMs) {
    if (this.timer !== null) {
      return; // a send - or a retry, after a failure - is already planned
    }

    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, delayMs);
  }

  retryLater() {
    this.retryMs = this.retryMs === 0 ? FIRST_RETRY_MS : Math.min(this.retryMs * 2, MAX_RETRY_MS);
    this.clearTimer();
    this.schedule(this.retryMs);
  }

  clearTimer() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  isAway(auth) {
    const last = this.readLastSeen();

    return last === null || last.memberId !== auth.memberId || Date.now() - last.at >= SESSION_GAP_MS;
  }

  stamp(event) {
    return { ...event, event_id: uuid(), occurred_at: new Date().toISOString(), platform: this.platform };
  }

  touch() {
    const auth = this.options.getAuth();
    if (auth !== null) {
      this.writeLastSeen({ memberId: auth.memberId, at: Date.now() });
    }
  }

  readOutbox() {
    const value = this.read(OUTBOX_KEY);
    if (value === undefined) {
      return this.memoryOutbox;
    }

    return isOutbox(value) ? value : null;
  }

  writeOutbox(outbox) {
    this.memoryOutbox = outbox;
    this.write(OUTBOX_KEY, outbox);
  }

  readLastSeen() {
    const value = this.read(LAST_SEEN_KEY);
    if (value === undefined) {
      return this.memoryLastSeen;
    }

    return isLastSeen(value) ? value : null;
  }

  writeLastSeen(lastSeen) {
    this.memoryLastSeen = lastSeen;
    this.write(LAST_SEEN_KEY, lastSeen);
  }

  /** The stored value, null when there is none, or undefined when storage is unusable. */
  read(key) {
    if (!this.storageWorks) {
      return undefined;
    }

    let raw;
    try {
      raw = window.localStorage.getItem(key);
    } catch {
      this.storageWorks = false; // e.g. blocked by the browser: carry on in memory
      return undefined;
    }

    if (raw === null) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  write(key, value) {
    if (!this.storageWorks) {
      return;
    }

    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch {
      this.storageWorks = false; // full, or blocked: carry on in memory
    }
  }
}

/** Resolves when `promise` does, or once `ms` have passed - whichever comes first. */
function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });

  return Promise.race([promise, timeout]).then(() => clearTimeout(timer));
}

function isOutbox(value) {
  return typeof value === "object" && value !== null && typeof value.memberId === "number" && Array.isArray(value.events);
}

function isLastSeen(value) {
  return typeof value === "object" && value !== null && typeof value.memberId === "number" && typeof value.at === "number";
}

/** A random (v4) UUID. crypto.randomUUID() only exists on https pages and newer browsers. */
export function uuid() {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
